import { TURNSTILE_SITE_KEYS } from 'src/constants';

import { appEnvControl } from 'utils';

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onloadTurnstileCallback';

/**
 * Give up waiting for Cloudflare rather than leaving the form unusable
 */
const SCRIPT_TIMEOUT = 10000;

let loadPromise: Promise<void> | null = null;

/**
 * @description
 * Turnstile site key for the current environment.
 * Empty when it has not been configured for this environment yet.
 *
 * The dev server does not pass APP_ENV, so the environment element can hold
 * the literal string 'undefined' — which is truthy and therefore survives
 * currentEnvWithDefaultDev(). Validate before using it as a key.
 */
function fetchTurnstileSiteKey(): string {
  const env = appEnvControl.currentEnv();
  const resolvedEnv: Environment = appEnvControl.isValidEnv(env) ? (env as Environment) : 'development';

  return TURNSTILE_SITE_KEYS[resolvedEnv] || '';
}

/**
 * @description
 * Turnstile is only active when a site key is configured for this environment.
 * This is the frontend kill switch; it says nothing about whether Cloudflare
 * could actually be reached (see turnstileStore.isUnavailable for that).
 */
function isTurnstileEnabled(): boolean {
  return Boolean(fetchTurnstileSiteKey());
}

/**
 * @description
 * Injects the Turnstile script once and resolves when window.turnstile is ready.
 * Repeated calls share the same promise.
 */
function loadTurnstileScript(): Promise<void> {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const timeoutId = window.setTimeout(() => {
      loadPromise = null;
      reject(new Error('Turnstile script timed out'));
    }, SCRIPT_TIMEOUT);

    /**
     * Cloudflare invokes this once window.turnstile is actually assigned.
     * script.onload only reports that the file arrived, which can fire before
     * the API object exists.
     */
    window.onloadTurnstileCallback = () => {
      window.clearTimeout(timeoutId);
      resolve();
    };

    if (document.getElementById(SCRIPT_ID)) {
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      window.clearTimeout(timeoutId);
      loadPromise = null;
      reject(new Error('Turnstile script failed to load'));
    };

    document.head && document.head.appendChild(script);
  });

  return loadPromise;
}

export { fetchTurnstileSiteKey, isTurnstileEnabled, loadTurnstileScript };
